import axios from "axios";
import { useEffect, useRef, useState, useCallback } from "react";

const STORAGE_KEY = "lastActivity";
const API_BASE = process.env.REACT_APP_BE_API_URL;

/**
 * Hook para detectar inactividad del usuario y ejecutar un callback tras expirar el tiempo.
 * Guarda la estampa de tiempo `lastActivity` en localStorage para persistir entre pestañas
 * y al reabrir el navegador.
 *
 * @param onTimeout Función a ejecutar cuando vence el tiempo de inactividad.
 * @param timeoutMs Tiempo en milisegundos de inactividad (por defecto 20 minutos).
 * @param warningMs Tiempo en milisegundos de la advertencia previa al timeout (por defecto 30 segundos).
 */
export function useInactivityTimeout(
  onTimeout: () => void,
  timeoutMs: number = 20 * 60 * 1000,
  warningMs: number = 30 * 1000
) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(Math.round(warningMs / 1000));
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  // Función para restablecer manualmente la sesión (por ejemplo, al hacer clic en "Seguir navegando")
  const resetTimeout = useCallback(async () => {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, now.toString());
    const url = `${API_BASE}/health`;
    await axios.get(url,);
    setShowWarning(false);
  }, []);

  useEffect(() => {
    let checkTimer: ReturnType<typeof setTimeout> | null = null;

    const checkSession = () => {
      const storedLastActivity = localStorage.getItem(STORAGE_KEY);
      const now = Date.now();

      if (!storedLastActivity) {
        localStorage.setItem(STORAGE_KEY, now.toString());
        scheduleNextCheck(timeoutMs - warningMs);
        return;
      }

      const lastActivityTime = parseInt(storedLastActivity, 10);
      const elapsedTime = now - lastActivityTime;
      const warningThreshold = timeoutMs - warningMs;

      if (elapsedTime >= timeoutMs) {
        // Tiempo de inactividad excedido, ejecutar el callback y limpiar el estado
        setShowWarning(false);
        localStorage.removeItem(STORAGE_KEY);
        onTimeoutRef.current();
      } else if (elapsedTime >= warningThreshold) {
        // Desplegar la advertencia de inactividad y el contador regresivo
        setShowWarning(true);
        const secondsRemaining = Math.max(0, Math.ceil((timeoutMs - elapsedTime) / 1000));
        setCountdown(secondsRemaining);

        if (secondsRemaining <= 0) {
          setShowWarning(false);
          localStorage.removeItem(STORAGE_KEY);
          onTimeoutRef.current();
          return;
        }

        // Chequear nuevamente en 1 segundo para ir decrementando el contador de forma precisa
        scheduleNextCheck(1000);
      } else {
        setShowWarning(false);
        scheduleNextCheck(warningThreshold - elapsedTime);
      }
    };

    const scheduleNextCheck = (ms: number) => {
      if (checkTimer) clearTimeout(checkTimer);
      checkTimer = setTimeout(checkSession, Math.max(0, ms));
    };

    const handleUserActivity = () => {
      // Si la advertencia de inactividad ya está mostrada en pantalla, ignoramos la actividad del mouse/teclado
      // y requerimos una confirmación explícita mediante el botón.
      if (showWarning) return;

      const now = Date.now();
      const storedLastActivity = localStorage.getItem(STORAGE_KEY);
      const lastActivityTime = storedLastActivity ? parseInt(storedLastActivity, 10) : 0;

      // Limitar la escritura en localStorage a un intervalo de cada 5 segundos
      if (now - lastActivityTime > 5000) {
        localStorage.setItem(STORAGE_KEY, now.toString());
      }
      checkSession();
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        checkSession();
      }
    };

    checkSession();

    const events: string[] = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });
    window.addEventListener("storage", handleStorageChange);

    return () => {
      if (checkTimer) clearTimeout(checkTimer);
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [timeoutMs, warningMs, showWarning]);

  return {
    showWarning,
    countdown,
    resetTimeout,
  };
}
